import { useState, useEffect } from 'react';
import { useCordova } from '@/core/hooks';

// Device type definitions
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// Screen size breakpoints (matching Tailwind CSS defaults)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export interface DeviceInfo {
  // Screen dimensions
  width: number;
  height: number;

  // Device type detection
  deviceType: DeviceType;

  // Responsive breakpoints
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isApp: boolean;

  // Tailwind breakpoint helpers
  isSmUp: boolean; // >= 640px
  isMdUp: boolean; // >= 768px
  isLgUp: boolean; // >= 1024px
  isXlUp: boolean; // >= 1280px
  is2XlUp: boolean; // >= 1536px

  // Orientation
  isPortrait: boolean;
  isLandscape: boolean;

  // Touch capability
  isTouchDevice: boolean;

  // Cordova integration
  isCordova: boolean;
  cordovaReady: boolean;
  platform: string | null;
}

/**
 * Custom hook for device detection and responsive design
 * Provides comprehensive device information including screen size, device type, and breakpoints
 *
 * @returns DeviceInfo object with device characteristics
 */
export const useDevice = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Initialize with default values for SSR compatibility
    if (typeof window === 'undefined') {
      return {
        width: 1024,
        height: 768,
        deviceType: 'desktop',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isApp: false,
        isSmUp: true,
        isMdUp: true,
        isLgUp: true,
        isXlUp: false,
        is2XlUp: false,
        isPortrait: false,
        isLandscape: true,
        isTouchDevice: false,
        isCordova: false,
        cordovaReady: false,
        platform: null,
      };
    }

    const deviceInfo = getDeviceInfo();
    return deviceInfo;
  });

  const cordovaInfo = useCordova();

  useEffect(() => {
    // Update device info on window resize
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo(cordovaInfo));
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Set initial values after component mount
    setDeviceInfo(getDeviceInfo(cordovaInfo));

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [cordovaInfo]);

  return deviceInfo;
};

/**
 * Helper function to calculate device information
 */
function getDeviceInfo(cordovaInfo?: {
  isCordova: boolean;
  deviceReady: boolean;
  platform: string | null;
}): DeviceInfo {
  const width = window.innerWidth;
  const height = window.innerHeight;

  // Determine device type based on screen width
  const deviceType: DeviceType =
    width < BREAKPOINTS.md
      ? 'mobile'
      : width < BREAKPOINTS.lg
        ? 'tablet'
        : 'desktop';

  // Calculate breakpoint flags
  const isSmUp = width >= BREAKPOINTS.sm;
  const isMdUp = width >= BREAKPOINTS.md;
  const isLgUp = width >= BREAKPOINTS.lg;
  const isXlUp = width >= BREAKPOINTS.xl;
  const is2XlUp = width >= BREAKPOINTS['2xl'];

  // Device type flags
  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop';
  const isApp = import.meta.env.VITE_BUILD_TARGET === 'app';

  // Orientation
  const isPortrait = height > width;
  const isLandscape = width > height;

  // Touch device detection
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Cordova information
  const isCordova = cordovaInfo?.isCordova || false;
  const cordovaReady = cordovaInfo?.deviceReady || false;
  const platform = cordovaInfo?.platform || null;

  return {
    width,
    height,
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isApp,
    isSmUp,
    isMdUp,
    isLgUp,
    isXlUp,
    is2XlUp,
    isPortrait,
    isLandscape,
    isTouchDevice,
    isCordova,
    cordovaReady,
    platform,
  };
}

// Additional utility hooks for specific use cases

/**
 * Hook that returns only the device type
 */
export const useDeviceType = (): DeviceType => {
  const { deviceType } = useDevice();
  return deviceType;
};

/**
 * Hook that returns only breakpoint information
 */
export const useBreakpoints = () => {
  const { isSmUp, isMdUp, isLgUp, isXlUp, is2XlUp } = useDevice();
  return { isSmUp, isMdUp, isLgUp, isXlUp, is2XlUp };
};

/**
 * Hook that returns only screen dimensions
 */
export const useScreenSize = () => {
  const { width, height } = useDevice();
  return { width, height };
};
